import { FormGroup, FormControl, Badge } from 'react-bootstrap';

import { Localization, SessionContext, SettingsManager, ViamapPersistenceLayer } from "@viamap/viamap2-common";
import { Utils } from '@viamap/viamap2-common';
import { MapInfo, DeploymentMode, MapExportImportFormat, ActiveFeatureLayerSpecs, AutocompleterKeys } from '../common/managers/Types';
import { AWSAPIGatewayInterface } from 'src/managers/AWSAPIGatewayInterface';
import { AWSUserAdmin } from 'src/common/managers/AWSUserAdmin';
import { useContext, useState } from 'react';
import { UserSession } from '@viamap/viamap2-common/dist/managers/Types';
import { ApplicationStateContext } from 'src/states/ApplicationState';
import { Feature } from 'src/states/ApplicationStateFeatures';
import { MapitStateContext } from 'src/states/MapitState';
import { FeatureLayerList } from 'src/managers/WmsLayerFunc';
import { FeatureLayers } from './GroupedLayerControl';
import { ADModalBody, ADModalFooter, ADModalInfo, AdvancedDragModal } from 'src/componentsUtils/AdvancedDragModal';
import { GlassButton } from './GlassButtons';
import { GlassCheckbox, GlassInfo } from './MitGlassComponents';
import { GlassFoldUdBox } from 'src/propertyInfoTemplates/PropertyComponents';
import { GlassInputControl, GlassInputGroup, GlassTextInputTransformer } from './GlassInput';
import { Typeahead } from 'react-bootstrap-typeahead';
import { ProtectedFeature } from './ProtectedFeature';

enum ValidityState {
    Success = "success",
    Warning = "warning",
    Error = "error",
}

type State = {
    mapInfo: MapInfo;
    showWindow: number;
    linkDurationText: string;
    minZoom: number;
    maxZoom: number;
    currentZoomLevel?: number;
    backgroundLayers: {};
    sessionInfo: UserSession;
    activeFeatureLayerKeys: any[];
    oldLinkOverwrite: boolean;
    oldLinkName: string;
    oldLinkNameMessage?: string;
    oldLinkNameValidationState?: ValidityState | null;
};

type Props = {
    mapInfo: MapInfo;
    showWindow: number;
    linkDuration: number;
    currentZoomLevel?: number;
    backgroundLayers: {};
    callbackOnCancel: () => void;
    callbackOnCommit: (mapInfo: MapInfo, linkDuration: number, minZoom: number, maxZoom: number, activeFeatureLayerKeys: any[], overWriteExstingLinkFile: boolean, oldLinkName?: string) => void;
};

export function SaveMapLinkDialog(props: Props) {
    const [state, _setState] = useState<State>(setFromProps());
    const { state: sessionState } = useContext(SessionContext);
    const { state: mapitState } = useContext(MapitStateContext);
    const { hasAccessToFeature } = useContext(ApplicationStateContext);
    const [activeFeatureLayerKeys, setActiveFeatureLayerKeys] = useState<string[]>(mapitState.selectedFeatureLayerKeys || []);

    function setState(b: any) {
        _setState((a) => ({ ...a, ...b }))
    }

    function setSearchBarAutocompleters(selected: any) {
        setState({ mapInfo: { ...state?.mapInfo, searchBarAutocompleters: selected } });
    }

    function setAllowMapPlot(allow: boolean) {
        setState({ mapInfo: { ...state?.mapInfo, allowMapPlot: allow } });
    }

    function setAllowLogin(allow: boolean) {
        setState({ mapInfo: { ...state?.mapInfo, allowLogin: allow } });
    }

    function setFromProps(): State {
        return {
            mapInfo: props.mapInfo,
            showWindow: props.showWindow,
            linkDurationText: props.linkDuration + "",
            currentZoomLevel: props.currentZoomLevel,
            backgroundLayers: props.backgroundLayers,
            sessionInfo: undefined as unknown as UserSession, // ToDo: still used?
            activeFeatureLayerKeys: [],
            minZoom: SettingsManager.getSystemSetting("minZoom", 18),
            maxZoom: SettingsManager.getSystemSetting("maxZoom", 18),
            oldLinkName: "",
            oldLinkNameValidationState: ValidityState.Error,
            oldLinkOverwrite: false,
            oldLinkNameMessage: Localization.getText("Savemap:oldLinkFormatHelp")
        }
    }


    function nameOnChange(event: any) {
        setState({ mapInfo: { ...state?.mapInfo, mapTitle: event.target.value } });
    }
    function onLinkOverwriteChange(e: any) {
        setState({ oldLinkOverwrite: e.target.checked });
    }

    function copyActiveFeatureLayerKeySelection(activeFeatureLayerSpecs: ActiveFeatureLayerSpecs) {
        if (activeFeatureLayerSpecs &&
            Object.keys(activeFeatureLayerSpecs).length > 0) {

            let featureLayers: FeatureLayerList = mapitState.featureLayers;
            let featureLayerKeys = {};
            if (featureLayers) {
                Object.keys(featureLayers).forEach((key) => {
                    let ftl = featureLayers[key].layer;
                    // First check for access rights and any isActive() logic.
                    let label = ftl.translationTable ? Localization.getTextSpecificTable(ftl.label, ftl.translationTable) : ftl.label;
                    featureLayerKeys[key] = label;
                });
            }

            let selectionState: {} = {};
            Object.keys(featureLayerKeys).forEach((val, idx) => {
                if (Object.keys(activeFeatureLayerSpecs).includes(val)) {
                    selectionState[idx] = val;
                }
            });
            setActiveFeatureLayerKeys(Object.values(selectionState))
        }
    }

    function oldLinkNameOnChange(ev: any) {
        let val = ev.target.value;
        setState({ oldLinkName: val });
        let msg = "";
        let vState = ValidityState.Success;

        if (val.length !== 36) {
            msg = Localization.getText("Savemap:oldLinkFormatHelp");
            vState = ValidityState.Error;
            setState({ oldLinkNameMessage: msg, oldLinkNameValidationState: vState });
        } else {
            // Read old link to se if we can safely overwrite it.
            let path = SettingsManager.getSystemSetting("mapLinkOnS3Path", "savedmaps/");
            AWSAPIGatewayInterface.loadStateS3(path + val)
                .then((result: MapExportImportFormat) => {
                    if (sessionState.userRef !== result.userId) {
                        // The map was saved by another user.

                        // If allowed and not development mode lookup user to check if the organization is identical
                        if (
                            SettingsManager.getSystemSetting("allowMapLinkOverwriteInSameOrganization", true)
                            && Utils.getDeploymentMode() !== DeploymentMode.Development
                        ) {
                            // Check which user created it. In same organization?
                            let auth = SettingsManager.getSystemSetting("authData");
                            let userPoolId = auth && auth.UserPoolId;
                            if (result.userId && userPoolId) {
                                AWSUserAdmin.getUser(userPoolId, result.userId)
                                    .then((userInfo) => {
                                        let userEmailRec = userInfo.UserAttributes.find((item: { Name: any, Value: any }) => {
                                            return item.Name === "email";
                                        });
                                        if (userEmailRec) {
                                            let userEmail = userEmailRec.Value;
                                            let savedLinkOrganization = ViamapPersistenceLayer.extractCustomerRef(userEmail);
                                            let myOrganization = ViamapPersistenceLayer.extractCustomerRef(sessionState.userRef);
                                            if (savedLinkOrganization === myOrganization) {
                                                let displayTime = (result.timeGenerated as Date).toLocaleDateString();
                                                let displayEndDate = result.endDate ? (result.endDate as Date).toLocaleDateString() : "";
                                                msg = Localization.getFormattedText("Savemap:oldLinkOtherUserInSameOrg", { time: displayTime, userName: result.userName, expires: displayEndDate });
                                                vState = ValidityState.Warning;
                                                setState({ oldLinkNameMessage: msg, oldLinkNameValidationState: vState });
                                                // Copy settings from saved link as default for changed link
                                                setState({
                                                    mapInfo: { ...state?.mapInfo, ...result.mapInfo }
                                                });
                                                minZoomOnChange(result.minZoom || state.minZoom);
                                                maxZoomOnChange(result.maxZoom || state.maxZoom);
                                                if (result.activeFeatureLayerSpecs) {
                                                    copyActiveFeatureLayerKeySelection(result.activeFeatureLayerSpecs);
                                                }
                                            } else {
                                                // user from a different organization
                                                msg = Localization.getText("Savemap:oldLinkOtherUserInDifferentOrg");
                                                vState = ValidityState.Error;
                                                setState({ oldLinkNameMessage: msg, oldLinkNameValidationState: vState });
                                            }
                                        } else {
                                            // could not find email for user
                                            msg = Localization.getText("Savemap:oldLinkOtherUser");
                                            vState = ValidityState.Error;
                                            setState({ oldLinkNameMessage: msg, oldLinkNameValidationState: vState });
                                        }
                                    })
                                    .catch((err3) => {
                                        msg = err3;
                                        vState = ValidityState.Error;
                                        setState({ oldLinkNameMessage: msg, oldLinkNameValidationState: vState });
                                    });
                            } else {
                                msg = Localization.getText("Savemap:AuthError");
                                vState = ValidityState.Error;
                                setState({ oldLinkNameMessage: msg, oldLinkNameValidationState: vState });
                            }
                        } else {
                            msg = Localization.getText("Savemap:oldLinkOtherUser");
                            vState = ValidityState.Error;
                            setState({ oldLinkNameMessage: msg, oldLinkNameValidationState: vState });
                        }
                    } else {
                        let displayTime = (result.timeGenerated as Date).toLocaleDateString();
                        let displayEndDate = result.endDate ? (result.endDate as Date).toLocaleDateString() : "";
                        msg = Localization.getFormattedText("Savemap:oldLinkOk", { time: displayTime, userName: result.userName, expires: displayEndDate });
                        vState = ValidityState.Success;
                        nameOnChange({ target: { value: result.mapInfo.mapTitle } })
                        if (result.endDate) {
                            numberOnChange({ target: { value: Math.round((result.endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) } })
                        }
                        setState({
                            oldLinkNameMessage: msg,
                            oldLinkNameValidationState: vState
                        });
                        // Copy settings from saved link as default for new changed link
                        setState({ mapInfo: { ...state?.mapInfo, ...result.mapInfo } });
                        minZoomOnChange(result.minZoom || state.minZoom);
                        maxZoomOnChange(result.maxZoom || state.maxZoom);
                        if (result.activeFeatureLayerSpecs) {
                            copyActiveFeatureLayerKeySelection(result.activeFeatureLayerSpecs);
                        }
                    }
                })
                .catch(() => {
                    msg = Localization.getText("Savemap:oldLinkNotFound");
                    vState = ValidityState.Error;
                    setState({ oldLinkNameMessage: msg, oldLinkNameValidationState: vState });
                });
        }
    }

    function numberOnChange(ev: any) {
        setState({ linkDurationText: ev.target.value });
    }

    function maxZoomOnChange(val: number) {
        setState({ maxZoom: val });
    }

    function minZoomOnChange(val: number) {
        setState({ minZoom: val });
    }

    function onFormSubmit(e: any) {
        // hide this window
        e?.preventDefault();
        let val = Utils.parseNumber(state.linkDurationText + "");
        let sourceDefault = SettingsManager.getSystemSetting("vectorSource", "Denmark");
        if (state.mapInfo.mapStyle.source == "Default" || !state.mapInfo.mapStyle.source) {
            state.mapInfo.mapStyle.source = sourceDefault;
        }
        props.callbackOnCommit(state.mapInfo, val || 0, state.minZoom, state.maxZoom, activeFeatureLayerKeys, state.oldLinkOverwrite, state.oldLinkName);
    }

    function validateFileName(): "success" | "warning" | "error" | null | undefined {
        return state.mapInfo.mapTitle && state.mapInfo.mapTitle.length > 0 ? "success" : "error";
    }

    function validateForm(): boolean {
        let validationState = state.oldLinkOverwrite ? state.oldLinkNameValidationState : "success";
        validationState = validationState === "success" ? validateFileName() : validationState;
        return validationState === "success" || validationState === "warning";
    }

    function handleCancel(e: any) {
        // hide this window
        e?.preventDefault();
        props.callbackOnCancel();
    }

    function onFeatureLayerChange(key: string, checked: boolean, layersKeys: { key: string }[]) {
        if (checked) {
            setActiveFeatureLayerKeys(pre => ([...new Set([...pre, ...layersKeys.map((a) => a.key)])]));
        } else {
            let keys = layersKeys.map((a) => a.key);
            setActiveFeatureLayerKeys(pre => (pre.filter((a) => !keys.includes(a))));
        }
    }

    function countOfSelectedLayers(): number {
        return activeFeatureLayerKeys ? activeFeatureLayerKeys.filter((key) => !mapitState.featureLayers[key].layer?.privateUseOnly).filter((key) => Boolean(key)).length : 0;
    }

    let availableSearchBarAutocompletersKeys: AutocompleterKeys[] = ["matrikel", "adresse", "lokalmatrikel", "lokaladresse", "stednavn", "postNr"];

    if (props.showWindow) {
        const zoomLevels = hasAccessToFeature(Feature.SaveLinkSetRestrictions) ? (
            <>
                <RowSimplifier label={Localization.getText("Max zoom level")}>
                    <ZoomLevelDropdown value={state.maxZoom} onChange={maxZoomOnChange} defaultZoomLevel={SettingsManager.getSystemSetting("maxZoom", 18)} />
                </RowSimplifier>
                <RowSimplifier label={Localization.getText("Min zoom level")}>
                    <ZoomLevelDropdown value={state.minZoom} onChange={minZoomOnChange} defaultZoomLevel={SettingsManager.getSystemSetting("minZoom", 3)} />
                </RowSimplifier>
            </>
        ) : null;



        const selectLayersToShare = hasAccessToFeature(Feature.SaveLinkSelectFeatureLayers) ? (
            <RowSimplifier top={"10px"} label={<>
                {Localization.getText("Give access to feature layers")}
                <br />
                <Badge style={{ borderRadius: 0 }} bg="success">
                    {countOfSelectedLayers() > 0 ? Localization.getFormattedText("Savemap:noOfLayersSelected", { count: countOfSelectedLayers() }) : null}
                </Badge></>}>
                {activeFeatureLayerKeys.filter((key) => mapitState.featureLayers[key].layer?.privateUseOnly).length ?
                    <Badge style={{ borderRadius: 0, width: "100%", fontWeight: "200" }} bg="danger" >
                        {Localization.getText("Following active layers can't be shared;")}<br />
                        {activeFeatureLayerKeys.filter((key) => mapitState.featureLayers[key].layer?.privateUseOnly).map((key) => {
                            let text = mapitState.featureLayers[key].layer.label;
                            let trText = mapitState.featureLayers[key].layer.translationTable?.[Localization.getLanguage()]?.[text] || text;
                            return trText
                        })}
                    </Badge>
                    : <></>
                }
                <div style={{ overflowY: "auto", overflowX: "hidden", flex: 1, maxHeight: "200px" }}>
                    <FeatureLayers
                        maxLayersToActivate={Number.MAX_SAFE_INTEGER}
                        layers={mapitState.featureLayers}
                        onChange={(a, b, c) => { onFeatureLayerChange(a, b, c) }}
                        selectedFeatureLayerKeys={activeFeatureLayerKeys}
                    />
                </div>
            </RowSimplifier>
        ) : undefined;

        const overWriteExstingLinkFile = hasAccessToFeature(Feature.SaveLinkReuseMapLink) ? (
            <RowSimplifier label={Localization.getText("Reuse/Overwrite existing link")} top='0px'>
                <div style={{ display: "flex", flexDirection: "column" }}>
                    <GlassCheckbox style={{ textAlign: "left", marginBottom: "5px" }} checked={state.oldLinkOverwrite} onClick={(e: any) => onLinkOverwriteChange(e)} >
                        {/* {Localization.getText("Savemap:overwrite link")} */}
                    </GlassCheckbox>
                    {state.oldLinkOverwrite ? (
                        <FormGroup
                            controlId="formBasicText"
                            // validationState={this.getOldLinkValidationState()} TODO:

                            style={{ margin: "0px", marginTop: "10px" }}
                        >
                            <FormControl
                                style={{ width: "100%" }}
                                size="sm"
                                type="text"
                                value={state.oldLinkName}
                                autoComplete="off"
                                placeholder={Localization.getText("Savemap:oldLinkName")}
                                onChange={(e) => oldLinkNameOnChange(e)}
                            />
                            <GlassInfo>{state.oldLinkNameMessage}</GlassInfo>
                        </FormGroup>
                    ) : null}
                </div>
            </RowSimplifier>
        ) : undefined;

        return (
            <AdvancedDragModal
                variant={"NSDark"}
                PosDefault={{ top: "100px", left: "50%", transform: "translateX(-50%)" }}
                title={Localization.getText("Generate map link")}

            >
                <ADModalInfo>
                    {Localization.getText("Savemaplink:description")}
                </ADModalInfo>

                <ADModalBody >
                    <GlassInputGroup
                        onEscape={(e) => handleCancel(e)}
                        autoFocus={0}
                    >
                        <RowSimplifier label={Localization.getText("Title")}>
                            <FormControl
                                style={{ width: "100%" }}
                                size="sm"
                                type="text"
                                value={state.mapInfo.mapTitle || ""}
                                autoComplete='off'
                                placeholder={Localization.getText("Savemap:defaultFileName")}
                                onChange={(e) => nameOnChange(e)}
                            />
                        </RowSimplifier>

                        <RowSimplifier label={Localization.getText("Link Duration")}>
                            <select
                                className="form-control"
                                id="sel1"
                                onChange={(e) => { numberOnChange(e); }}
                                value={state.linkDurationText}
                            >
                                <option value="1">1 {Localization.getText("day")}</option>
                                <option value="7">1 {Localization.getText("week")}</option>
                                <option value="30">1 {Localization.getText("month")}</option>
                                <option value="60">2 {Localization.getText("months")}</option>
                                <option value="90">3 {Localization.getText("months")}</option>
                                <option value="182">6 {Localization.getText("months")}</option>
                                <option value="365">12 {Localization.getText("months")}</option>
                                {["1", "7", "30", "60", "90", "182", "365"].includes(state.linkDurationText) ? <></> :
                                    <option value={state.linkDurationText}>{state.linkDurationText} {Localization.getText("days")}</option>
                                }
                            </select>
                        </RowSimplifier>
                    </GlassInputGroup>

                    {/* <GlassFoldUdBox title={Localization.getText("Search bar")} foldetUd={false} variant='Small' >
                                            <GlassInputGroup>
                                        
                                        </GlassInputGroup>
                                    </GlassFoldUdBox> */}
                    <GlassFoldUdBox
                        title={Localization.getText("Expander:AdvancedSettings")}
                        foldetUd={false}
                        variant='Small'
                    >
                        <GlassInputGroup>
                            {overWriteExstingLinkFile}
                            <ProtectedFeature feature={Feature.SaveLinkMetaData} contentsIfNoAccess={<></>}>
                                <RowSimplifier label={Localization.getText("Savemap:metadata")}>
                                    <GlassCheckbox checked={state.mapInfo.includeMetadata} onClick={(e: any) => { setState({ mapInfo: { ...state?.mapInfo, includeMetadata: e.target.checked } }); }}>
                                        {Localization.getText("Savemap:includeMetadata")}
                                    </GlassCheckbox>
                                </RowSimplifier>
                            </ProtectedFeature>
                            {zoomLevels}
                            {selectLayersToShare}
                            <ProtectedFeature feature={Feature.SaveLinkAllowLogin} contentsIfNoAccess={<></>}>
                                <RowSimplifier label={Localization.getText("Allow login")}>
                                    <GlassCheckbox checked={state.mapInfo.allowLogin} onClick={(e: any) => { setAllowLogin(e.target.checked); }}>
                                        {Localization.getText("Savemap:allowLogin")}
                                    </GlassCheckbox>
                                </RowSimplifier>
                            </ProtectedFeature>
                            <ProtectedFeature feature={Feature.SaveLinkAllowMapPlot} contentsIfNoAccess={<></>}>
                                <RowSimplifier label={Localization.getText("Map Plot")}>
                                    <GlassCheckbox checked={state.mapInfo.allowMapPlot} onClick={(e: any) => { setAllowMapPlot(e.target.checked); }}>
                                        {Localization.getText("Savemap:allowMapPlot")}
                                    </GlassCheckbox>
                                </RowSimplifier>
                            </ProtectedFeature>
                            <ProtectedFeature feature={Feature.SaveLinkShowUserLocation} contentsIfNoAccess={<></>}>
                                <RowSimplifier label={Localization.getText("Show my location")}>
                                    <GlassCheckbox checked={state.mapInfo.showUserLocation} onClick={(e: any) => { setState({ mapInfo: { ...state?.mapInfo, showUserLocation: e.target.checked } }); }}>
                                        {Localization.getText("Savemap:showUserLocation")}
                                    </GlassCheckbox>
                                </RowSimplifier>
                            </ProtectedFeature>
                            <ProtectedFeature feature={Feature.SaveLinkSelectSearchOptions} contentsIfNoAccess={<></>}>
                                <RowSimplifier label={Localization.getText("Search options")}>
                                    <Typeahead
                                        id={"mytypeahead"}
                                        multiple={true}
                                        options={
                                            availableSearchBarAutocompletersKeys.map((a) => {
                                                return { label: Localization.getText("SearchBar:" + a), value: a }
                                            })
                                        }
                                        labelKey={"label"}
                                        // dropup={true}
                                        minLength={0}
                                        positionFixed={true}
                                        placeholder={Localization.getText("Start typing to select options")}
                                        emptyLabel={Localization.getText("No matches found")}
                                        onChange={(selected: any) => {
                                            setSearchBarAutocompleters(selected.map((a) => a.value));
                                        }}
                                        selected={(state.mapInfo?.searchBarAutocompleters || []).map((a) => { return { label: Localization.getText("SearchBar:" + a), value: a } })}
                                    />
                                </RowSimplifier>
                                {/* Requires SaveLinkSelectSearchBehavior and SaveLinkSelectSearchOptions */}
                                <ProtectedFeature feature={Feature.SaveLinkSelectSearchBehaviour} contentsIfNoAccess={<></>}>
                                    <RowSimplifier label={Localization.getText("Search Behaviour")}>
                                        <GlassInputControl>
                                            <select disabled={!state.mapInfo?.searchBarAutocompleters?.length} onChange={(e => { setState({ mapInfo: { ...state?.mapInfo, searchBarBehaviour: e.target.value } }); })} value={state.mapInfo.searchBarBehaviour}>
                                                <option value="zoomToResult">{Localization.getText("Zoom to result")}</option>
                                                <option value="zoomToNearest">{Localization.getText("Zoom to nearest")}</option>
                                                <option value="openNearest">{Localization.getText("Open nearest")}</option>
                                            </select>
                                        </GlassInputControl>
                                    </RowSimplifier>
                                </ProtectedFeature>
                            </ProtectedFeature>
                        </GlassInputGroup>
                    </GlassFoldUdBox>

                </ADModalBody>
                <ADModalFooter>
                    <GlassButton onClick={(e) => onFormSubmit(e)} disabled={!validateForm()} >{Localization.getText("button:GenerateLink")}</GlassButton>
                    <GlassButton onClick={(e) => handleCancel(e)} >{Localization.getText("Cancel")}</GlassButton>
                </ADModalFooter>
            </AdvancedDragModal>
        );
    } else {
        return (null);
    }

}


function RowSimplifier(props: { label, children, top?: string }) {
    return <GlassTextInputTransformer label={props.label} top={props.top} >{props.children}</GlassTextInputTransformer>
}

function ZoomLevelDropdown(props: { value: number, onChange: (value: number) => void, defaultZoomLevel: number }) {
    let options = Array.from({ length: 23 }, (_, i) => i + 1).reverse().map((index) => {
        return { value: index, text: index + (index === props.defaultZoomLevel ? " (Default)" : "") }
    });

    return (
        <select
            className="form-control"
            id="numerical-dropdown"
            onChange={(e) => props.onChange(parseInt(e.target.value, 10))}
            value={props.value}
        >
            {options.map((option) => (
                <option key={option.value} value={option.value}>{option.text}</option>
            ))}
        </select>
    );
}